import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  STANDARD_ZIEL_BRANCH,
  type MetarrCodeSperrAktion,
  type ReasoningStufe,
} from "@cubic/shared";
import { Flaeche } from "@/components/entwurf/Flaeche";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Pruefquadrat } from "@/components/entwurf/Pruefquadrat";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zustandsmarke } from "@/components/entwurf/Zustandsmarke";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { schluessel } from "@/lib/abfragen";
import { api } from "@/lib/api";
import { fehlerMeldung } from "@/lib/haken";
import {
  baueMetarrCodeBranch,
  istMetarrCodeArbeitspaketAktiv,
  istSichererMetarrCodeBranch,
  reasoningStufeName,
} from "@/lib/metarr-arbeitspaket";
import {
  einzelnesProjekt,
  useProjektAuswahl,
} from "@/lib/projekt-auswahl";
import type { Meldung } from "@/lib/meldung";
import { cx, focusRing } from "@/lib/utils";
import {
  ArbeitspaketPatchVorschau,
  hatVollstaendigePatchVorschau,
  type ArbeitspaketAenderung,
} from "./ArbeitspaketPatchVorschau";

type BranchModus = "neu" | "vorhanden";
type Budget = 500 | 1_000;
type ListenSeite = Awaited<
  ReturnType<typeof api.metarr.codeArbeitspakete.query>
>;
type Lauf = ListenSeite["eintraege"][number];
type LaufDetail = Awaited<
  ReturnType<typeof api.metarr.codeArbeitspaket.query>
>;
type KiAuswahl = {
  verbindungId: string;
  modell: string;
  reasoningStufe: ReasoningStufe;
};
type Entscheidung =
  | { art: "freigeben"; lauf: LaufDetail }
  | { art: "ablehnen"; lauf: LaufDetail; grund: string }
  | { art: "abbrechen"; lauf: LaufDetail; grund: string }
  | { art: "wiederholen"; lauf: LaufDetail };

const EINGABE =
  "mt-1.5 w-full rounded-none border border-linie bg-papier px-3 py-2 text-[13px] text-tinte shadow-none outline-none focus:border-akzent focus:ring-1 focus:ring-akzent disabled:bg-wisch disabled:text-tinte4";

const ABBRECHBAR = new Set(["wartet_planung", "plant"]);

function status(zustand: string): {
  name: string;
  hinweis: string;
  farbe: "gruen" | "rot" | "blau" | "gelb" | "grau";
} {
  switch (zustand) {
    case "wartet_planung":
      return {
        name: "Wartet auf Planung",
        hinweis: "Der Worker übernimmt den begrenzten Auftrag.",
        farbe: "gelb",
      };
    case "plant":
      return {
        name: "Planung läuft",
        hinweis: "Metarr liest nur den ausgewählten, sicheren Kontext.",
        farbe: "blau",
      };
    case "bestaetigung_offen":
      return {
        name: "Prüfung erforderlich",
        hinweis: "Der Patch ist eingefroren und wartet auf deine Entscheidung.",
        farbe: "gelb",
      };
    case "veroeffentlichung_wartet":
      return {
        name: "Veröffentlichung wartet",
        hinweis: "Der freigegebene Patch wird unverändert nach GitLab geschrieben.",
        farbe: "blau",
      };
    case "commit_erstellt":
      return {
        name: "Commit erstellt",
        hinweis: "Der Commit steht; der Merge Request wird noch geöffnet.",
        farbe: "blau",
      };
    case "veroeffentlicht":
      return {
        name: "Merge Request geöffnet",
        hinweis: "Die weitere Merge-Freigabe gehört zu den Quality Gates.",
        farbe: "gruen",
      };
    case "fertig_ohne_mr":
      return {
        name: "Commit erstellt",
        hinweis: "Wie gewählt wurde kein Merge Request angelegt.",
        farbe: "gruen",
      };
    case "abgelehnt":
      return {
        name: "Abgelehnt",
        hinweis: "Der eingefrorene Patch wird nicht veröffentlicht.",
        farbe: "grau",
      };
    case "abgebrochen":
      return {
        name: "Abgebrochen",
        hinweis: "Der Lauf wurde vor einer Veröffentlichung beendet.",
        farbe: "grau",
      };
    case "veroeffentlichung_gescheitert":
      return {
        name: "MR fehlgeschlagen",
        hinweis: "Der Commit bleibt erhalten; nur der MR-Schritt kann wiederholt werden.",
        farbe: "rot",
      };
    case "zu_gross":
      return {
        name: "Zu groß",
        hinweis: "Die harte Zeilengrenze hat die Veröffentlichung gestoppt.",
        farbe: "rot",
      };
    case "konflikt":
      return {
        name: "Ausgangsstand geändert",
        hinweis: "Eine SHA-Prüfung hat stilles Überschreiben verhindert.",
        farbe: "rot",
      };
    default:
      return {
        name: "Fehlgeschlagen",
        hinweis: "Der Lauf wurde ohne weitere GitLab-Schritte beendet.",
        farbe: "rot",
      };
  }
}

function neuerWiederholungsschluessel(): string {
  const id = globalThis.crypto?.randomUUID?.();
  return id
    ? "web:" + id
    : "web:" + Date.now().toString(36) + ":" + Math.random().toString(36).slice(2);
}

function datumZeit(wert: Date | string): string {
  const datum = wert instanceof Date ? wert : new Date(wert);
  if (Number.isNaN(datum.getTime())) return "Unbekannter Zeitpunkt";
  return datum.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function Feld({
  label,
  htmlFor,
  kinder,
  hinweis,
}: {
  label: string;
  htmlFor?: string;
  kinder: ReactNode;
  hinweis?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="block">
        <Mikrolabel grad={12} ton="laut" betont>
          {label}
        </Mikrolabel>
      </label>
      {kinder}
      {hinweis ? (
        <p className="mt-1.5 text-[11.5px] leading-[1.5] text-tinte4">{hinweis}</p>
      ) : null}
    </div>
  );
}

function Knopf({
  children,
  ton = "sekundaer",
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  ton?: "primaer" | "sekundaer" | "gefahr";
}) {
  return (
    <button
      type="button"
      className={cx(
        "inline-flex min-h-10 items-center justify-center border px-3 py-2 text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45",
        ton === "primaer" &&
          "border-tinte bg-tinte text-papier hover:border-akzent hover:bg-akzent",
        ton === "sekundaer" &&
          "border-linie bg-transparent text-tinte hover:border-tinte",
        ton === "gefahr" &&
          "border-[var(--zustand-fehler)] bg-transparent text-[var(--zustand-fehler)] hover:bg-[color-mix(in_oklab,var(--zustand-fehler)_10%,transparent)]",
        focusRing,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function Fehlermeldung({
  meldung,
  onNeuLaden,
}: {
  meldung: Meldung;
  onNeuLaden?: () => void;
}) {
  return (
    <div
      className="border-l-2 border-[var(--zustand-fehler)] bg-wisch px-4 py-3"
      role="alert"
      data-testid="metarr-code-arbeitspaket-fehler"
    >
      <p className="text-[13px] leading-[1.55] text-tinte">{meldung.text}</p>
      {meldung.neuLadenAnbieten && onNeuLaden ? (
        <Knopf className="mt-3" onClick={onNeuLaden}>
          Neu laden
        </Knopf>
      ) : null}
    </div>
  );
}

/*
  Der Weg aus der Sperre. Intern wird geroutet, extern öffnet GitLab in einem
  eigenen Tab — wer den Zugang einrichtet, verliert dabei die Seite nicht.
  Kennt niemand die Adresse, bleibt der Hinweis für sich stehen.
*/
function Sperraktion({ aktion }: { aktion: MetarrCodeSperrAktion | null }) {
  if (!aktion) return null;
  const klasse = cx(
    "mt-2.5 inline-block text-[12px] font-medium text-akzent hover:underline",
    focusRing,
  );
  if (aktion.extern) {
    return (
      <a
        href={aktion.url}
        target="_blank"
        rel="noopener noreferrer"
        className={klasse}
      >
        {aktion.text}
      </a>
    );
  }
  return (
    <Link to={aktion.url} className={klasse}>
      {aktion.text}
    </Link>
  );
}

function Auswahl({
  aktiv,
  titel,
  hinweis,
  onKlick,
  disabled,
}: {
  aktiv: boolean;
  titel: string;
  hinweis: string;
  onKlick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={aktiv}
      disabled={disabled}
      onClick={onKlick}
      className={cx(
        "min-w-0 border-l-2 px-3 py-2.5 text-left disabled:opacity-45",
        aktiv ? "border-akzent bg-wisch" : "border-linie bg-transparent",
        focusRing,
      )}
    >
      <span className="block text-[13px] font-medium text-tinte">{titel}</span>
      <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-tinte4">
        {hinweis}
      </span>
    </button>
  );
}

function LaufDetails({
  lauf,
  patchGeprueft,
  setzePatchGeprueft,
  grund,
  setzeGrund,
  entscheiden,
  laeuft,
}: {
  lauf: LaufDetail;
  patchGeprueft: boolean;
  setzePatchGeprueft: (wert: boolean) => void;
  grund: string;
  setzeGrund: (wert: string) => void;
  entscheiden: (aktion: Entscheidung) => void;
  laeuft: boolean;
}) {
  const darstellung = status(lauf.zustand);
  const aenderungen =
    (lauf.modellPlan?.aenderungen ?? []) as ArbeitspaketAenderung[];
  const patchVollstaendig = hatVollstaendigePatchVorschau(
    aenderungen,
    lauf.dateien.map((datei) => datei.pfad),
  );
  const grundGueltig = grund.trim().length >= 5 && grund.trim().length <= 2_000;

  return (
    <div
      className="min-w-0 bg-wisch px-3 py-5 sm:px-5"
      data-testid={"metarr-code-arbeitspaket-detail-" + lauf.id}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Zustandsmarke farbe={darstellung.farbe}>{darstellung.name}</Zustandsmarke>
          <p className="mt-2 max-w-[64ch] text-[13px] leading-[1.55] text-tinte3">
            {darstellung.hinweis}
          </p>
        </div>
        <Mikrolabel zahl>v{lauf.version}</Mikrolabel>
      </div>

      <dl className="mt-5 grid gap-x-6 gap-y-3 border-y border-linie py-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Modell", lauf.modell + " · " + reasoningStufeName(lauf.reasoningStufe)],
          ["Branch", lauf.quellBranch + " → " + lauf.zielBranch],
          ["Ausgangs-SHA", lauf.branchStartSha?.slice(0, 12) ?? "—"],
          [
            "Umfang",
            "+" + lauf.hinzugefuegteZeilen + " / −" + lauf.entfernteZeilen +
              " von " + lauf.maxZeilen,
          ],
        ].map(([label, wert]) => (
          <div key={label} className="min-w-0">
            <dt><Mikrolabel>{label}</Mikrolabel></dt>
            <dd className="mt-1 break-words font-mono text-[11.5px] text-tinte2">
              {wert}
            </dd>
          </div>
        ))}
      </dl>

      {lauf.modellPlan ? (
        <div className="mt-5">
          <h3 className="text-[15px] font-medium text-tinte">
            {lauf.modellPlan.titel}
          </h3>
          <p className="mt-1 max-w-[68ch] text-[13px] leading-[1.6] text-tinte3">
            {lauf.modellPlan.zusammenfassung}
          </p>
        </div>
      ) : null}

      {aenderungen.length > 0 ? (
        <div className="mt-6 min-w-0">
          <ArbeitspaketPatchVorschau aenderungen={aenderungen} />
        </div>
      ) : null}

      {lauf.modellPlan?.pruefhinweise.length ? (
        <div className="mt-5">
          <Mikrolabel grad={12} ton="laut" betont>Prüfhinweise des Modells</Mikrolabel>
          <ul className="mt-2 space-y-1.5 text-[12.5px] leading-[1.5] text-tinte3">
            {lauf.modellPlan.pruefhinweise.map((hinweis, index) => (
              <li key={index}>— {hinweis}</li>
            ))}
          </ul>
          <p className="mt-2 text-[11.5px] text-tinte4">
            Hinweise sind keine Testergebnisse. Cubic Studio führt keine Befehle oder Tests aus.
          </p>
        </div>
      ) : null}

      {lauf.letzterFehler ? (
        <div className="mt-5 border-l-2 border-[var(--zustand-fehler)] bg-papier px-3 py-2.5">
          <Mikrolabel ton="laut" betont>Letzter Fehler</Mikrolabel>
          <p className="mt-1 break-words text-[12.5px] leading-[1.5] text-tinte2">
            {lauf.letzterFehler}
          </p>
        </div>
      ) : null}

      {lauf.zustand === "bestaetigung_offen" ? (
        <div className="mt-6 border-t border-tinte pt-5">
          <div className="flex items-start gap-3">
            <Pruefquadrat
              erfuellt={patchGeprueft}
              onKlick={() => setzePatchGeprueft(!patchGeprueft)}
              ariaLabel="Ich habe alle gezeigten Dateien und Änderungen geprüft"
            />
            <button
              type="button"
              className={cx("text-left text-[13px] leading-[1.5] text-tinte", focusRing)}
              onClick={() => setzePatchGeprueft(!patchGeprueft)}
            >
              Ich habe alle gezeigten Dateien und Änderungen geprüft.
            </button>
          </div>
          {!patchVollstaendig ? (
            <p className="mt-3 text-[12px] text-[var(--zustand-fehler)]" role="alert">
              Die Patchvorschau ist unvollständig. Eine Freigabe ist gesperrt.
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Knopf
              ton="primaer"
              disabled={!patchVollstaendig || !patchGeprueft || laeuft}
              onClick={() => entscheiden({ art: "freigeben", lauf })}
              data-testid={"metarr-arbeitspaket-freigeben-" + lauf.id}
            >
              Eingefrorenen Patch freigeben
            </Knopf>
          </div>
          <div className="mt-5 max-w-[620px]">
            <Feld
              label="Ablehnungsgrund"
              htmlFor={"ablehnungsgrund-" + lauf.id}
              kinder={
                <Textarea
                  id={"ablehnungsgrund-" + lauf.id}
                  rows={3}
                  maxLength={2_000}
                  value={grund}
                  onChange={(ereignis) => setzeGrund(ereignis.target.value)}
                  className="mt-1.5 rounded-none border-linie bg-papier shadow-none"
                  placeholder="Warum soll dieser Patch nicht veröffentlicht werden?"
                />
              }
            />
            <Knopf
              ton="gefahr"
              className="mt-2"
              disabled={!grundGueltig || laeuft}
              onClick={() =>
                entscheiden({ art: "ablehnen", lauf, grund: grund.trim() })
              }
            >
              Patch ablehnen
            </Knopf>
          </div>
        </div>
      ) : null}

      {ABBRECHBAR.has(lauf.zustand) ? (
        <div className="mt-6 border-t border-linie pt-4">
          <Feld
            label="Abbruchgrund"
            htmlFor={"abbruchgrund-" + lauf.id}
            kinder={
              <Textarea
                id={"abbruchgrund-" + lauf.id}
                rows={2}
                maxLength={2_000}
                value={grund}
                onChange={(ereignis) => setzeGrund(ereignis.target.value)}
                className="mt-1.5 rounded-none border-linie bg-papier shadow-none"
                placeholder="Warum wird der Lauf nicht mehr benötigt?"
              />
            }
          />
          <Knopf
            ton="gefahr"
            className="mt-2"
            disabled={!grundGueltig || laeuft}
            onClick={() =>
              entscheiden({ art: "abbrechen", lauf, grund: grund.trim() })
            }
          >
            Lauf abbrechen
          </Knopf>
        </div>
      ) : null}

      {lauf.zustand === "gescheitert" &&
      !lauf.commitSha &&
      !lauf.freigegebenAm ? (
        <div className="mt-6 border-t border-linie pt-4">
          <p className="max-w-[64ch] text-[12.5px] leading-[1.5] text-tinte3">
            Die Planung ist endgültig gescheitert. Eine bewusste Wiederholung
            verwendet denselben eingefrorenen GitLab-Ausgangsstand und startet
            ausschließlich den Modellschritt neu.
          </p>
          <Knopf
            className="mt-3"
            disabled={laeuft}
            onClick={() => entscheiden({ art: "wiederholen", lauf })}
          >
            Planung wiederholen
          </Knopf>
        </div>
      ) : null}

      {lauf.zustand === "veroeffentlichung_gescheitert" ? (
        <div className="mt-6 border-t border-linie pt-4">
          <p className="max-w-[64ch] text-[12.5px] leading-[1.5] text-tinte3">
            Der vorhandene Commit bleibt unverändert. Die Wiederholung versucht ausschließlich,
            den offenen Merge Request zu finden oder anzulegen.
          </p>
          <Knopf
            className="mt-3"
            disabled={laeuft}
            onClick={() => entscheiden({ art: "wiederholen", lauf })}
          >
            MR-Veröffentlichung wiederholen
          </Knopf>
        </div>
      ) : null}

      {lauf.commitUrl || lauf.mergeRequestUrl ? (
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 border-t border-linie pt-4">
          {lauf.commitUrl ? (
            <a
              href={lauf.commitUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cx("text-[12.5px] font-medium text-akzent hover:underline", focusRing)}
            >
              Commit in GitLab öffnen
            </a>
          ) : null}
          {lauf.mergeRequestUrl ? (
            <a
              href={lauf.mergeRequestUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cx("text-[12.5px] font-medium text-akzent hover:underline", focusRing)}
              data-testid={"metarr-arbeitspaket-mr-" + lauf.id}
            >
              Merge Request {lauf.mergeRequestIid ? "!" + lauf.mergeRequestIid : ""} öffnen
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function CodeArbeitspaketAbschnitt() {
  const queryClient = useQueryClient();
  const { auswahl } = useProjektAuswahl();
  const projektId = einzelnesProjekt(auswahl);
  const [beschreibung, setzeBeschreibung] = useState("");
  const [rezeptFassungId, setzeRezeptFassungId] = useState<string | null>(null);
  const [budget, setzeBudget] = useState<Budget>(500);
  const [branchModus, setzeBranchModus] = useState<BranchModus>("neu");
  const [neuerBranch, setzeNeuerBranch] = useState("");
  const [bestehenderBranch, setzeBestehenderBranch] = useState<string | null>(null);
  const [mergeRequestErstellen, setzeMergeRequestErstellen] = useState(true);
  const [kiAuswahl, setzeKiAuswahl] = useState<KiAuswahl | null>(null);
  const [detailId, setzeDetailId] = useState<string | null | undefined>(undefined);
  const [patchGeprueft, setzePatchGeprueft] = useState(false);
  const [grund, setzeGrund] = useState("");
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [wiederholungsschluessel, setzeWiederholungsschluessel] = useState(
    neuerWiederholungsschluessel,
  );

  useEffect(() => {
    setzeBeschreibung("");
    setzeRezeptFassungId(null);
    setzeBudget(500);
    setzeBranchModus("neu");
    setzeNeuerBranch("");
    setzeBestehenderBranch(null);
    setzeMergeRequestErstellen(true);
    setzeKiAuswahl(null);
    setzeDetailId(undefined);
    setzePatchGeprueft(false);
    setzeGrund("");
    setzeMeldung(null);
    setzeWiederholungsschluessel(neuerWiederholungsschluessel());
  }, [projektId]);

  const optionen = useQuery({
    queryKey: schluessel.metarrCodeArbeitspaketOptionen(projektId ?? ""),
    queryFn: () =>
      api.metarr.codeArbeitspaketOptionen.query({ projektId: projektId! }),
    enabled: Boolean(projektId),
  });
  const laeufe = useInfiniteQuery({
    queryKey: schluessel.metarrCodeArbeitspakete(projektId ?? ""),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.metarr.codeArbeitspakete.query({
        projektId: projektId!,
        cursor: pageParam,
        grenze: 20,
      }),
    getNextPageParam: (seite) => seite.naechsterCursor ?? undefined,
    enabled: Boolean(projektId),
    refetchInterval: (abfrage) =>
      abfrage.state.data?.pages.some((seite) =>
        seite.eintraege.some((lauf) =>
          istMetarrCodeArbeitspaketAktiv(lauf.zustand),
        ),
      )
        ? 3_000
        : false,
  });
  const laufListe = useMemo(
    () => laeufe.data?.pages.flatMap((seite) => seite.eintraege) ?? [],
    [laeufe.data?.pages],
  );
  const aktuellesDetailId =
    detailId === undefined ? laufListe[0]?.id ?? null : detailId;
  const detail = useQuery({
    queryKey: schluessel.metarrCodeArbeitspaket(aktuellesDetailId ?? ""),
    queryFn: () =>
      api.metarr.codeArbeitspaket.query({
        arbeitspaketId: aktuellesDetailId!,
      }),
    enabled: Boolean(aktuellesDetailId),
    refetchInterval: (abfrage) =>
      abfrage.state.data &&
      istMetarrCodeArbeitspaketAktiv(abfrage.state.data.zustand)
        ? 3_000
        : false,
  });

  useEffect(() => {
    setzePatchGeprueft(false);
    setzeGrund("");
  }, [detail.data?.id, detail.data?.version]);

  const standard: KiAuswahl | null = optionen.data?.standard
    ? {
        verbindungId: optionen.data.standard.verbindungId,
        modell: optionen.data.standard.modell,
        reasoningStufe: optionen.data.standard.reasoningStufe,
      }
    : null;
  const ausfuehrung = kiAuswahl ?? standard;
  const verbindung = optionen.data?.verbindungen.find(
    (eintrag) => eintrag.id === ausfuehrung?.verbindungId,
  );
  const modell = verbindung?.modelle.find(
    (eintrag) => eintrag.id === ausfuehrung?.modell,
  );
  const rezept =
    rezeptFassungId ?? optionen.data?.empfohlenesRezeptFassungId ?? null;
  const branchVorschlag = optionen.data
    ? baueMetarrCodeBranch(optionen.data.projekt.kuerzel, beschreibung)
    : "feat/code-arbeitspaket";
  const neuerBranchName = neuerBranch || branchVorschlag;
  const vorhandenerBranchName =
    bestehenderBranch ?? optionen.data?.branches[0]?.name ?? "";
  const branchName =
    branchModus === "neu" ? neuerBranchName : vorhandenerBranchName;
  const zielBranch =
    optionen.data?.projekt.zielBranch ?? STANDARD_ZIEL_BRANCH;
  const branchGueltig =
    istSichererMetarrCodeBranch(branchName, zielBranch) &&
    (branchModus === "neu" ||
      optionen.data?.branches.some((eintrag) => eintrag.name === branchName));
  const formularGueltig = Boolean(
    projektId &&
      optionen.data?.darfStarten &&
      beschreibung.trim().length >= 20 &&
      beschreibung.trim().length <= 20_000 &&
      rezept &&
      ausfuehrung &&
      modell &&
      branchGueltig,
  );

  const start = useMutation({
    mutationFn: () =>
      api.metarr.codeArbeitspaketStarten.mutate({
        projektId: projektId!,
        beschreibung: beschreibung.trim(),
        rezeptFassungId: rezept!,
        maxZeilen: budget,
        branch: { modus: branchModus, name: branchName },
        mergeRequestErstellen,
        verbindungId: ausfuehrung!.verbindungId,
        modell: ausfuehrung!.modell,
        reasoningStufe: ausfuehrung!.reasoningStufe,
        wiederholungsschluessel,
      }),
    onSuccess: async (lauf) => {
      setzeMeldung(null);
      setzeDetailId(lauf.id);
      setzeBeschreibung("");
      setzeNeuerBranch("");
      setzeWiederholungsschluessel(neuerWiederholungsschluessel());
      await queryClient.invalidateQueries({
        queryKey: schluessel.metarrCodeArbeitspakete(lauf.projektId),
      });
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  const entscheidung = useMutation({
    mutationFn: async (aktion: Entscheidung) => {
      const basis = {
        arbeitspaketId: aktion.lauf.id,
        erwarteteVersion: aktion.lauf.version,
      };
      if (aktion.art === "freigeben") {
        return api.metarr.codeArbeitspaketFreigeben.mutate(basis);
      }
      if (aktion.art === "ablehnen") {
        return api.metarr.codeArbeitspaketAblehnen.mutate({
          ...basis,
          grund: aktion.grund,
        });
      }
      if (aktion.art === "abbrechen") {
        return api.metarr.codeArbeitspaketAbbrechen.mutate({
          ...basis,
          grund: aktion.grund,
        });
      }
      return api.metarr.codeArbeitspaketWiederholen.mutate(basis);
    },
    onSuccess: async (lauf) => {
      setzeMeldung(null);
      setzeGrund("");
      setzePatchGeprueft(false);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: schluessel.metarrCodeArbeitspakete(lauf.projektId),
        }),
        queryClient.invalidateQueries({
          queryKey: schluessel.metarrCodeArbeitspaket(lauf.id),
        }),
      ]);
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  return (
    <section
      className="mb-13 mt-13 max-w-[880px] min-w-0"
      data-testid="metarr-code-arbeitspakete"
    >
      <Rubrik anzahl={projektId ? "max. 1.000 Zeilen" : "Projekt wählen"}>
        Code-Arbeitspakete
      </Rubrik>
      <p className="mt-4 max-w-[66ch] text-[14px] font-light leading-[1.65] text-tinte3">
        Metarr bereitet einen kleinen, prüfbaren Patch vor. Der Ausgangsstand bleibt
        eingefroren; erst deine ausdrückliche Dateiprüfung erlaubt Commit und
        optionalen Merge Request nach {zielBranch}.
      </p>

      {!projektId ? (
        <Flaeche className="mt-5">
          <Mikrolabel ton="laut" betont>Genau ein Projekt auswählen</Mikrolabel>
          <p className="mt-2 max-w-[58ch] text-[13px] leading-[1.55] text-tinte3">
            Ein Code-Arbeitspaket braucht eine eindeutige GitLab-Zuordnung. Wähle in
            der Projektauswahl statt „Alle Projekte“ ein einzelnes Projekt.
          </p>
        </Flaeche>
      ) : optionen.isLoading ? (
        <p className="mt-5 text-[13px] text-tinte4" role="status">
          GitLab-, Rezept- und Modelloptionen werden geladen …
        </p>
      ) : optionen.isError ? (
        <div className="mt-5">
          <Fehlermeldung
            meldung={fehlerMeldung(optionen.error)}
            onNeuLaden={() => void optionen.refetch()}
          />
        </div>
      ) : optionen.data ? (
        <Flaeche className="mt-5" polster={false}>
          <div className="border-b border-linie px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <Mikrolabel ton="laut" betont>{optionen.data.projekt.name}</Mikrolabel>
                <p className="mt-1 break-all font-mono text-[11.5px] text-tinte4">
                  {optionen.data.projekt.gitlabProjekt ?? "Keine GitLab-Zuordnung"}
                </p>
              </div>
              {optionen.data.projekt.gitlabUrl ? (
                <a
                  href={optionen.data.projekt.gitlabUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cx("text-[12px] font-medium text-akzent hover:underline", focusRing)}
                >
                  Repository öffnen
                </a>
              ) : null}
            </div>
          </div>

          <div className="space-y-6 px-4 py-5 sm:px-6">
            {optionen.data.sperre ? (
              <div
                className="border-l-2 border-[var(--zustand-wartet)] bg-papier px-4 py-3"
                role="alert"
                data-testid="metarr-arbeitspaket-blocker"
              >
                <Mikrolabel ton="laut" betont>Start gesperrt</Mikrolabel>
                <p className="mt-1 text-[13px] leading-[1.55] text-tinte3">
                  {optionen.data.sperre.grund}
                </p>
                <p className="mt-1.5 text-[12.5px] leading-[1.55] text-tinte4">
                  {optionen.data.sperre.hinweis}
                </p>
                <Sperraktion aktion={optionen.data.sperre.aktion} />
              </div>
            ) : null}
            {meldung ? (
              <Fehlermeldung
                meldung={meldung}
                onNeuLaden={() => {
                  void detail.refetch();
                  void laeufe.refetch();
                }}
              />
            ) : null}

            <Feld
              label="Begrenzter Auftrag"
              htmlFor="metarr-code-beschreibung"
              kinder={
                <Textarea
                  id="metarr-code-beschreibung"
                  rows={6}
                  maxLength={20_000}
                  value={beschreibung}
                  onChange={(ereignis) => {
                    setzeBeschreibung(ereignis.target.value);
                    setzeMeldung(null);
                  }}
                  disabled={!optionen.data.darfStarten}
                  className="mt-1.5 rounded-none border-linie bg-papier shadow-none"
                  placeholder="Beschreibe eine kleine, in sich geschlossene Codeänderung …"
                />
              }
              hinweis={
                beschreibung.length.toLocaleString("de-DE") +
                " / 20.000 Zeichen · mindestens 20"
              }
            />

            <div className="grid min-w-0 gap-5 md:grid-cols-2">
              <Feld
                label="Unveränderliches Rezept"
                htmlFor="metarr-code-rezept"
                kinder={
                  <select
                    id="metarr-code-rezept"
                    className={EINGABE}
                    value={rezept ?? ""}
                    disabled={!optionen.data.darfStarten}
                    onChange={(ereignis) => setzeRezeptFassungId(ereignis.target.value)}
                  >
                    {optionen.data.rezepte.map((eintrag) => (
                      <option key={eintrag.fassungId} value={eintrag.fassungId}>
                        {eintrag.name} · v{eintrag.version}
                      </option>
                    ))}
                  </select>
                }
                hinweis="Die gewählte Fassung bleibt am Lauf gespeichert."
              />
              <div>
                <Mikrolabel grad={12} ton="laut" betont>Zeilenbudget</Mikrolabel>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <Auswahl
                    aktiv={budget === 500}
                    titel="Bis 500"
                    hinweis="Kompakter Standard"
                    onKlick={() => setzeBudget(500)}
                    disabled={!optionen.data.darfStarten}
                  />
                  <Auswahl
                    aktiv={budget === 1_000}
                    titel="Bis 1.000"
                    hinweis="Harte Obergrenze"
                    onKlick={() => setzeBudget(1_000)}
                    disabled={!optionen.data.darfStarten}
                  />
                </div>
              </div>
            </div>

            <div>
              <Mikrolabel grad={12} ton="laut" betont>Quellbranch</Mikrolabel>
              <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                <Auswahl
                  aktiv={branchModus === "neu"}
                  titel="Neuer Branch"
                  hinweis={`Startet exakt von ${zielBranch}`}
                  onKlick={() => setzeBranchModus("neu")}
                  disabled={!optionen.data.darfStarten}
                />
                <Auswahl
                  aktiv={branchModus === "vorhanden"}
                  titel="Vorhandener Branch"
                  hinweis="Nur ungeschützt und beschreibbar"
                  onKlick={() => setzeBranchModus("vorhanden")}
                  disabled={!optionen.data.darfStarten || optionen.data.branches.length === 0}
                />
              </div>
              {branchModus === "neu" ? (
                <Input
                  aria-label="Neuer Branchname"
                  value={neuerBranchName}
                  maxLength={255}
                  disabled={!optionen.data.darfStarten}
                  onChange={(ereignis) => setzeNeuerBranch(ereignis.target.value)}
                  className="mt-3"
                  inputClassName="rounded-none border-linie bg-papier font-mono text-xs shadow-none"
                />
              ) : (
                <select
                  aria-label="Vorhandener Branch"
                  className={cx(EINGABE, "mt-3 font-mono")}
                  value={vorhandenerBranchName}
                  disabled={!optionen.data.darfStarten}
                  onChange={(ereignis) => setzeBestehenderBranch(ereignis.target.value)}
                >
                  {optionen.data.branches.map((eintrag) => (
                    <option key={eintrag.name} value={eintrag.name}>
                      {eintrag.name}
                    </option>
                  ))}
                </select>
              )}
              {!branchGueltig && branchName ? (
                <p className="mt-2 text-[12px] text-[var(--zustand-fehler)]" role="alert">
                  Dieser Branchname ist ungültig, geschützt oder nicht mehr verfügbar.
                </p>
              ) : null}
            </div>

            <div className="grid min-w-0 gap-4 md:grid-cols-3">
              <Feld
                label="Anbieterverbindung"
                htmlFor="metarr-code-verbindung"
                kinder={
                  <select
                    id="metarr-code-verbindung"
                    className={EINGABE}
                    value={ausfuehrung?.verbindungId ?? ""}
                    disabled={!optionen.data.darfStarten}
                    onChange={(ereignis) => {
                      const naechste = optionen.data.verbindungen.find(
                        (eintrag) => eintrag.id === ereignis.target.value,
                      );
                      const erstesModell = naechste?.modelle[0];
                      if (!naechste || !erstesModell) return;
                      setzeKiAuswahl({
                        verbindungId: naechste.id,
                        modell: erstesModell.id,
                        reasoningStufe: erstesModell.empfohleneReasoningStufe,
                      });
                    }}
                  >
                    {optionen.data.verbindungen.map((eintrag) => (
                      <option
                        key={eintrag.id}
                        value={eintrag.id}
                        disabled={eintrag.modelle.length === 0}
                      >
                        {eintrag.name}
                      </option>
                    ))}
                  </select>
                }
              />
              <Feld
                label="Modell"
                htmlFor="metarr-code-modell"
                kinder={
                  <select
                    id="metarr-code-modell"
                    className={EINGABE}
                    value={ausfuehrung?.modell ?? ""}
                    disabled={!optionen.data.darfStarten || !verbindung}
                    onChange={(ereignis) => {
                      const naechstesModell = verbindung?.modelle.find(
                        (eintrag) => eintrag.id === ereignis.target.value,
                      );
                      if (!ausfuehrung || !naechstesModell) return;
                      setzeKiAuswahl({
                        ...ausfuehrung,
                        modell: naechstesModell.id,
                        reasoningStufe: naechstesModell.empfohleneReasoningStufe,
                      });
                    }}
                  >
                    {verbindung?.modelle.map((eintrag) => (
                      <option key={eintrag.id} value={eintrag.id}>
                        {eintrag.id}
                      </option>
                    ))}
                  </select>
                }
              />
              <Feld
                label="Reasoning"
                htmlFor="metarr-code-reasoning"
                kinder={
                  <select
                    id="metarr-code-reasoning"
                    className={EINGABE}
                    value={ausfuehrung?.reasoningStufe ?? "none"}
                    disabled={!optionen.data.darfStarten || !modell}
                    onChange={(ereignis) => {
                      if (!ausfuehrung) return;
                      setzeKiAuswahl({
                        ...ausfuehrung,
                        reasoningStufe: ereignis.target.value as ReasoningStufe,
                      });
                    }}
                  >
                    {modell?.reasoningStufen.map((stufe) => (
                      <option key={stufe} value={stufe}>
                        {reasoningStufeName(stufe)}
                      </option>
                    ))}
                  </select>
                }
              />
            </div>

            <div className="flex items-start gap-3 border-y border-linie py-4">
              <Pruefquadrat
                erfuellt={mergeRequestErstellen}
                onKlick={() => setzeMergeRequestErstellen(!mergeRequestErstellen)}
                ariaLabel="Nach Freigabe einen Merge Request erstellen"
              />
              <button
                type="button"
                className={cx("text-left", focusRing)}
                onClick={() => setzeMergeRequestErstellen(!mergeRequestErstellen)}
              >
                <span className="block text-[13px] font-medium text-tinte">
                  Nach Freigabe einen Merge Request öffnen
                </span>
                <span className="mt-0.5 block text-[11.5px] leading-[1.5] text-tinte4">
                  Ziel ist unveränderlich {zielBranch}. Mergen darf dieses Paket nicht.
                </span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Knopf
                ton="primaer"
                disabled={!formularGueltig || start.isPending}
                onClick={() => start.mutate()}
                data-testid="metarr-arbeitspaket-starten"
              >
                {start.isPending ? "Arbeitspaket startet …" : "Code-Arbeitspaket starten"}
              </Knopf>
              <Mikrolabel>Kein GitLab-Schreiben ohne spätere Dateiprüfung.</Mikrolabel>
            </div>
          </div>
        </Flaeche>
      ) : null}

      {projektId ? (
        <div className="mt-10">
          <Rubrik
            anzahl={laufListe.length + (laeufe.hasNextPage ? "+" : "")}
            aktion={
              laeufe.isFetching && !laeufe.isLoading ? (
                <Mikrolabel ariaLabel="Liste wird aktualisiert">aktualisiert …</Mikrolabel>
              ) : null
            }
          >
            Letzte Läufe
          </Rubrik>
          {laeufe.isLoading ? (
            <p className="py-5 text-[13px] text-tinte4">Läufe werden geladen …</p>
          ) : laeufe.isError ? (
            <div className="mt-4">
              <Fehlermeldung
                meldung={fehlerMeldung(laeufe.error)}
                onNeuLaden={() => void laeufe.refetch()}
              />
            </div>
          ) : laufListe.length === 0 ? (
            <Flaeche className="mt-4">
              <Mikrolabel ton="laut" betont>Noch kein Code-Arbeitspaket</Mikrolabel>
              <p className="mt-2 text-[13px] leading-[1.55] text-tinte3">
                Der erste gestartete Planlauf erscheint hier mit seinem eingefrorenen Stand.
              </p>
            </Flaeche>
          ) : (
            <ul data-testid="metarr-arbeitspaket-liste">
              {laufListe.map((lauf: Lauf) => {
                const darstellung = status(lauf.zustand);
                const offen = aktuellesDetailId === lauf.id;
                return (
                  <li key={lauf.id} className="min-w-0 border-b border-linie">
                    <button
                      type="button"
                      className={cx(
                        "grid w-full min-w-0 gap-2 py-4 text-left sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center",
                        focusRing,
                      )}
                      aria-expanded={offen}
                      onClick={() => setzeDetailId(offen ? null : lauf.id)}
                    >
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-[13.5px] font-medium leading-[1.45] text-tinte">
                          {lauf.modellTitel || lauf.beschreibung}
                        </span>
                        <span className="mt-1 block break-all font-mono text-[11px] text-tinte4">
                          {lauf.quellBranch} · {datumZeit(lauf.angelegtAm)}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2 sm:justify-end">
                        <Mikrolabel zahl>
                          {lauf.dateiAnzahl} Dateien · +{lauf.hinzugefuegteZeilen} / −
                          {lauf.entfernteZeilen}
                        </Mikrolabel>
                        <Zustandsmarke farbe={darstellung.farbe}>
                          {darstellung.name}
                        </Zustandsmarke>
                      </span>
                    </button>
                    {offen ? (
                      detail.isLoading ? (
                        <p className="bg-wisch px-4 py-5 text-[13px] text-tinte4">
                          Eingefrorener Patch wird geladen …
                        </p>
                      ) : detail.isError ? (
                        <div className="bg-wisch p-4">
                          <Fehlermeldung
                            meldung={fehlerMeldung(detail.error)}
                            onNeuLaden={() => void detail.refetch()}
                          />
                        </div>
                      ) : detail.data ? (
                        <LaufDetails
                          lauf={detail.data}
                          patchGeprueft={patchGeprueft}
                          setzePatchGeprueft={setzePatchGeprueft}
                          grund={grund}
                          setzeGrund={setzeGrund}
                          entscheiden={(aktion) => entscheidung.mutate(aktion)}
                          laeuft={entscheidung.isPending}
                        />
                      ) : null
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {laeufe.hasNextPage ? (
            <Knopf
              className="mt-4"
              disabled={laeufe.isFetchingNextPage}
              onClick={() => void laeufe.fetchNextPage()}
            >
              {laeufe.isFetchingNextPage ? "Weitere Läufe laden …" : "Weitere Läufe laden"}
            </Knopf>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
